/**
 * api.js — Single source of truth for backend URL and authenticated fetch.
 *
 * Import in every component:
 *   import { API, apiFetch } from '../lib/api';          // most files
 *   import { API, apiFetch } from '../../lib/api';       // nested dirs (erp/, erpWorkspace/)
 *
 * API    = "<REACT_APP_BACKEND_URL>/api"  (use for all endpoint URLs)
 * BACKEND= "<REACT_APP_BACKEND_URL>"      (raw origin, rarely needed)
 * apiFetch(url, opts?, token?)           — fetch + auto JWT header
 * authFetch                              — alias of apiFetch (backward compat)
 */

const _raw = process.env.REACT_APP_BACKEND_URL;
// export const BACKEND = _raw ? _raw.trim().replace(/\/+$/, '') : _raw;

function resolveApiBase() {
  if (typeof window === "undefined") {
    // SSR safety - use env or default
    return _raw || "http://localhost:8002";
  }

  const { hostname } = window.location;

  // 1. CHECK: Are we on retailcliq.cloud domain? (PRODUCTION)
  if (hostname.includes('retailcliq.cloud')) {
    console.log('🔵 Production mode detected - using same-origin:', window.location.origin);
    return window.location.origin;
  }

  // 2. CHECK: Are we on localhost? (DEVELOPMENT)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Use env URL if set, otherwise default to localhost:8001
    const devUrl = _raw || 'http://localhost:8002';
    console.log('🟢 Development mode detected - using:', devUrl);
    return devUrl;
  }

  // 3. FALLBACK: If environment variable is set, use itsss
  if (_raw) {
    console.log('🟡 Using environment URL:', _raw);
    return _raw;
  }

  // 4. ULTIMATE FALLBACK: Same origin
  console.log('🟣 Fallback to same-origin:', window.location.origin);
  return window.location.origin;
}

export const BACKEND = resolveApiBase();

export const API = `${BACKEND}/api`;

const TOKEN_KEY = 'erp_security_token';

function getStoredToken() {
  return (
    sessionStorage.getItem(TOKEN_KEY) ||
    localStorage.getItem(TOKEN_KEY) ||
    null
  );
}

/**
 * apiFetch — authenticated fetch wrapper.
 * @param {string}  url   Full URL — build with the API constant.
 * @param {object}  opts  Standard fetch options (method, body, headers…).
 * @param {string}  [token] Explicit JWT — falls back to stored session token.
 */
export function apiFetch(url, opts = {}, token) {
  const t = token || getStoredToken();
  return fetch(url, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
  });
}

/** authFetch — alias of apiFetch (backward compatibility). */
export const authFetch = apiFetch;
